import { useEffect, useRef, useState } from "react";
import { ConnectButton } from "@rainbow-me/rainbowkit";
import { useDisconnect } from "wagmi";
import { ChevronDown, Globe, LogOut, User } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useEthBalance } from "@/hooks/useEthBalance";

function formatBalance(value: string | null) {
  if (!value) return "— ETH";
  const n = parseFloat(value);
  if (Number.isNaN(n)) return "— ETH";
  return `${n.toFixed(4)} ETH`;
}

export function HeaderWalletButton({ className }: { className?: string }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const balance = useEthBalance();
  const { disconnect } = useDisconnect();

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setMenuOpen(false);
      }
    }
    if (menuOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [menuOpen]);

  return (
    <div className={className}>
      <ConnectButton.Custom>
        {({
          account,
          chain,
          openAccountModal,
          openChainModal,
          openConnectModal,
          mounted,
        }) => {
          const ready = mounted;
          const connected = ready && account && chain;

          if (!connected) {
            return (
              <Button
                variant="pixel"
                size="pixel"
                className="w-full md:w-auto"
                onClick={openConnectModal}
              >
                Connect
              </Button>
            );
          }

          if (chain.unsupported) {
            return (
              <Button
                variant="pixel"
                size="pixel"
                className="w-full bg-danger hover:bg-danger/90 md:w-auto"
                onClick={openChainModal}
              >
                Wrong Network
              </Button>
            );
          }

          return (
            <div ref={menuRef} className="relative">
              <Button
                variant="pixel"
                size="pixel"
                className="flex w-full items-center gap-2 font-mono md:w-auto"
                onClick={() => setMenuOpen((v) => !v)}
              >
                <span className="hidden text-muted-foreground sm:inline">
                  {formatBalance(balance)}
                </span>
                <span className="text-foreground">{account.displayName}</span>
                <ChevronDown
                  size={14}
                  className={cn(
                    "transition-transform",
                    menuOpen && "rotate-180"
                  )}
                />
              </Button>

              {menuOpen && (
                <div className="absolute right-0 top-full z-50 mt-2 w-48 rounded-xl border border-border bg-card p-1 shadow-xl">
                  <button
                    onClick={() => {
                      setMenuOpen(false);
                      openAccountModal();
                    }}
                    className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-sm text-foreground transition-colors hover:bg-primary/10"
                  >
                    <User size={14} className="text-primary" />
                    Account
                  </button>
                  <button
                    onClick={() => {
                      setMenuOpen(false);
                      openChainModal();
                    }}
                    className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-sm text-foreground transition-colors hover:bg-primary/10"
                  >
                    <Globe size={14} className="text-primary" />
                    Network
                  </button>
                  <button
                    onClick={() => {
                      setMenuOpen(false);
                      disconnect();
                    }}
                    className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-sm text-danger transition-colors hover:bg-danger/10"
                  >
                    <LogOut size={14} />
                    Disconnect
                  </button>
                </div>
              )}
            </div>
          );
        }}
      </ConnectButton.Custom>
    </div>
  );
}
